import Cookies from 'js-cookie';
import React, { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { useFeatureIsOn } from 'src/common/growthbook';
import { detectBrowserLanguage } from 'src/common/helpers/language-utils';
export enum SupportedLanguage {
  ENGLISH = 'en',
  FRENCH = 'fr',
}
interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  isUserPreference: boolean;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
const LANGUAGE_PREFERENCE_COOKIE_KEY = 'thm-user-language-preference';
const getStoredLanguagePreference = (): SupportedLanguage | null => {
  const stored = Cookies.get(LANGUAGE_PREFERENCE_COOKIE_KEY);
  if (stored && Object.values(SupportedLanguage).includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage;
  }
  return null;
};
const hasUserPreference = (): boolean => {
  return Cookies.get(LANGUAGE_PREFERENCE_COOKIE_KEY) !== undefined;
};
interface LanguageProviderProps {
  children: ReactNode;
}
export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children
}) => {
  const isFrenchFeatureFlagEnabled = useFeatureIsOn('is-french-language');
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(() => {
    // Priority: User preference > Feature flag > Browser detection > Default (English)
    const userPreference = getStoredLanguagePreference();
    if (userPreference) {
      return userPreference;
    }
    if (isFrenchFeatureFlagEnabled && detectBrowserLanguage()) {
      return SupportedLanguage.FRENCH;
    }
    return SupportedLanguage.ENGLISH;
  });
  const [userPreference, setUserPreference] = useState<boolean>(hasUserPreference);

  // Update language when feature flag changes (only if user hasn't set a preference)
  useEffect(() => {
    if (!userPreference) {
      const newLanguage = isFrenchFeatureFlagEnabled && detectBrowserLanguage() ? SupportedLanguage.FRENCH : SupportedLanguage.ENGLISH;
      if (newLanguage !== currentLanguage) {
        setCurrentLanguage(newLanguage);
      }
    }
  }, [isFrenchFeatureFlagEnabled, userPreference, currentLanguage]);
  const setLanguage = (language: SupportedLanguage) => {
    if (language === currentLanguage) {
      return;
    }
    setCurrentLanguage(language);
    setUserPreference(true);

    // Store the language preference
    Cookies.set(LANGUAGE_PREFERENCE_COOKIE_KEY, language, {
      expires: 365
    });
    window.location.reload();
  };
  const contextValue: LanguageContextType = {
    currentLanguage,
    setLanguage,
    isUserPreference: userPreference
  };
  return <LanguageContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="LanguageProvider" data-sentry-source-file="language-context.tsx">{children}</LanguageContext.Provider>;
};
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Utility function to check if current language is French
export const useIsFrench = (): boolean => {
  const {
    currentLanguage
  } = useLanguage();
  return currentLanguage === SupportedLanguage.FRENCH;
};